/**
 * CommunityStandardsModal — the community-standards read-and-acknowledge
 * act (voice-ladder gate, Skip ruling 2026-07-07; SCOPING §7.5/§7.9 as
 * amended: the acknowledgment moved from the signup checkbox to this
 * deliberate Getting-started act).
 *
 * A deliberate sibling of EulaModal: same chrome (shared CSS module),
 * same bundled-markdown rendering, same proof-of-read scroll gate, same
 * versioned accept POST. Reading the standards and pressing Accept IS
 * the acknowledgment — there is no bare "acknowledge" that skips the
 * reading (§7.9 pointer-vs-content discipline: this modal is the
 * content; the checklist row is only the pointer).
 *
 * On accept: POST /api/auth/accept-standards → the security cache is
 * invalidated (see src/api/standards.ts) → the Getting-started row
 * completes and org-scoped posting unlocks.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { acceptStandards } from '../../api/standards';
import {
  STANDARDS_CURRENT_VERSION,
  STANDARDS_CURRENT_VERSION_LABEL,
} from '../../content/legal/standardsConstants';
// Shared chrome + markdown renderer — deliberately reused from EulaModal so
// the two versioned-agreement surfaces stay visually identical.
import { renderMarkdown } from '../EulaModal/EulaModal';
import styles from '../EulaModal/EulaModal.module.css';
// @ts-ignore — Vite ?raw type augmentation lives in vite-env.d.ts
import standardsMarkdown from '../../content/legal/community-standards.en.md?raw';
import { sanitizeRichText } from '../../utils/sanitizeHtml';

export interface CommunityStandardsModalProps {
  open: boolean;
  /** Called after a successful acknowledgment. */
  onAccepted: () => void;
  /** Close without acknowledging (always allowed — this act is invited,
   *  never forced; the checklist row simply stays pending). */
  onClose: () => void;
}

const CommunityStandardsModal: React.FC<CommunityStandardsModalProps> = ({
  open,
  onAccepted,
  onClose,
}) => {
  const { t } = useAppTranslation('pages');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrolledEnough, setScrolledEnough] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bodyHtml = React.useMemo(() => renderMarkdown(standardsMarkdown), []);

  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const distance = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight;
    if (total <= el.clientHeight) {
      setScrolledEnough(true);
      return;
    }
    if (distance / total >= 0.8) {
      setScrolledEnough(true);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setScrolledEnough(false);
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(handleScroll);
    }
  }, [open, handleScroll]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptStandards(STANDARDS_CURRENT_VERSION);
      setSubmitting(false);
      onAccepted();
    } catch (err: any) {
      setError(err?.message || t('standardsModal.errorGeneric'));
      setSubmitting(false);
    }
  }, [onAccepted, t]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="standards-modal-title"
      >
        <div className={styles.header}>
          <div>
            <h1 id="standards-modal-title" className={styles.title}>
              {t('standardsModal.title')}
            </h1>
            <span className={styles.versionLabel}>
              {STANDARDS_CURRENT_VERSION_LABEL}
            </span>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t('standardsModal.close')}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body — scrollable standards text. Bundled markdown we authored,
            still sanitized at the render boundary (one policy for every
            dangerouslySetInnerHTML sink). */}
        <div
          ref={bodyRef}
          className={styles.body}
          onScroll={handleScroll}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(bodyHtml) }}
        />

        <div className={styles.footer}>
          {error && (
            <div role="alert" className={styles.errorMessage}>
              {error}
            </div>
          )}
          {!scrolledEnough && (
            <div className={styles.scrollHint}>{t('standardsModal.scrollHint')}</div>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              {t('standardsModal.cancel')}
            </button>
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={handleAccept}
              disabled={!scrolledEnough || submitting}
            >
              {submitting
                ? t('standardsModal.submitting')
                : t('standardsModal.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityStandardsModal;
